import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { STEP_BRAND, STEP_PROFILE, STEP_PROMPTS, getProjectSetupState } from './state';

const PREFIX = 'ai_tracking.onboarding';

const STEP_LABELS = {
    [STEP_BRAND]: `${PREFIX}.steps.brand`,
    [STEP_PROFILE]: `${PREFIX}.steps.profile`,
    [STEP_PROMPTS]: `${PREFIX}.steps.prompts`,
};

/**
 * The way back into setup once the welcome modal is gone.
 *
 * It reports progress rather than storing it -- the ticks are read off the project every render
 * -- so a step finished by hand in project settings is already ticked here, and the whole
 * control disappears on its own once there is nothing left to do. Only an unfinished step is
 * clickable: a finished one has nothing to reopen, and offering it would invite the user to redo
 * work that is already done.
 */
class SetupChecklist extends Component {
    constructor() {
        super();

        this.onToggle = this.onToggle.bind(this);
        this.onClickOutside = this.onClickOutside.bind(this);

        this.containerRef = React.createRef();
        this.state = { isOpen: false };
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.onClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.onClickOutside);
    }

    onClickOutside(event) {
        if (!this.state.isOpen) return;
        if (this.containerRef.current?.contains(event.target)) return;
        this.setState({ isOpen: false });
    }

    onToggle() {
        this.setState((prevState) => ({ isOpen: !prevState.isOpen }));
    }

    onSelectStep(step) {
        this.setState({ isOpen: false });
        this.props.onOpenStep(step);
    }

    render() {
        const { t, project } = this.props;
        const setup = getProjectSetupState(project);

        if (!project || setup.isComplete) return null;

        return (
            <div className="onboarding-checklist" ref={this.containerRef}>
                <button
                    type="button"
                    className="onboarding-checklist-trigger"
                    onClick={this.onToggle}
                    aria-expanded={this.state.isOpen}
                >
                    <span>{t(`${PREFIX}.checklist.title`)}</span>
                    <span className="onboarding-checklist-progress">
                        {t(`${PREFIX}.checklist.progress`, {
                            done: setup.doneCount,
                            total: setup.totalSteps,
                        })}
                    </span>
                    <i className="icon-chevron-right" />
                </button>
                {this.state.isOpen && (
                    <div className="onboarding-checklist-dropdown">
                        {[STEP_BRAND, STEP_PROFILE, STEP_PROMPTS].map((step) => {
                            const done = setup.steps[step];

                            return (
                                <button
                                    key={step}
                                    type="button"
                                    className={`onboarding-checklist-item ${done ? 'is-done' : ''}`}
                                    disabled={done}
                                    onClick={() => this.onSelectStep(step)}
                                >
                                    <span className="onboarding-checklist-mark">
                                        {done ? <i className="icon-check-icon" /> : step}
                                    </span>
                                    <span>{t(STEP_LABELS[step])}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
}

export default withTranslation()(SetupChecklist);
